import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => {
  const isAbsolute = className?.includes("absolute");
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "pointer-events-none inset-0 z-50 bg-foreground/45 data-[state=open]:pointer-events-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        isAbsolute ? "absolute" : "fixed",
        className
      )}
      {...props}
    />
  );
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  hideCloseButton?: boolean
  container?: HTMLElement | null
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, style, hideCloseButton = false, container, ...props }, ref) => {
  const contentStyle = React.useMemo<React.CSSProperties>(
    () => {
      if (container) {
        const rect = container.getBoundingClientRect();
        const leftCenter = rect.left + rect.width / 2;
        return {
          position: "fixed",
          top: "50%",
          left: `${leftCenter}px`,
          transform: "translate3d(-50%, -50%, 0)",
          width: `min(var(--overlay-max-width), ${rect.width * 0.96}px)`,
          maxWidth: `min(var(--overlay-max-width), ${rect.width * 0.96}px)`,
          maxHeight: "95vh",
          ...style,
        };
      }
      return {
        position: "fixed",
        width: `min(var(--overlay-max-width), calc(var(--app-dvw) - var(--overlay-safe-inline) * 2))`,
        maxWidth: `min(var(--overlay-max-width), calc(var(--app-dvw) - var(--overlay-safe-inline) * 2))`,
        maxHeight: "calc(var(--app-dvh) - var(--app-safe-area-top) - var(--app-safe-area-bottom) - var(--overlay-safe-block) * 2)",
        ...style,
      };
    },
    [style, container]
  )

  return (
    <DialogPortal container={container}>
      <DialogOverlay className={container ? "absolute" : "fixed"} />
      <DialogPrimitive.Content
        ref={ref}
        style={contentStyle}
        className={cn(
          "overlay-surface overlay-dimensions overlay-centered overlay-elevated overlay-scrollable overlay-frame z-50 border border-border bg-card duration-200 focus-visible:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          className
        )}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className="absolute right-4 top-4 border border-border bg-card p-2 opacity-70 ring-offset-background transition-opacity hover:border-primary hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:ring-offset-0 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "font-serif text-2xl font-light leading-tight tracking-tight text-foreground",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
